import { ApolloProvider } from "@apollo/client";
import { Auth0Provider } from "@auth0/auth0-react";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import NProgress from "nprogress";
import { useEffect, useId, useMemo, useRef } from "react";

import { Toaster } from "@/components/ui/sonner";

import "@/globals.css";
import "nprogress/nprogress.css";

import AbandonCart from "@/components/abandon-cart";
import AuthModal from "@/components/auth-modal";
import ExploreCategoryModal from "@/components/explore-category-modal";
import Footer from "@/components/footer";
import Intercom from "@/components/intercom";
import OfflineBanner from "@/components/offline-banner";
import LocationProvider from "@/contexts/location";
import apollo from "@/helpers/apollo";
import { AnalyticsProvider, Pageview } from "@/hooks/use-analytics";
import { TokenProvider } from "@/hooks/use-auth";
import { HistoryManagerProvider, useHistoryManager } from "@/hooks/use-history";
import { useServiceWorker } from "@/hooks/use-service-worker";
import ErrorBoundary from "@/pages/errors/error-boundary";

NProgress.configure({ showSpinner: false, speed: 500 });

export const Route = createRootRoute({
  component: RootComponent,
  head: () => ({
    links: [
      {
        href: "/images/icons/apple-touch-icon.png",
        rel: "apple-touch-icon",
        sizes: "180x180",
      },
      {
        href: "/images/icons/favicons/favicon-96x96.png",
        rel: "icon",
        sizes: "96x96",
        type: "image/png",
      },
      {
        href: "/images/icons/favicons/favicon-48x48.png",
        rel: "icon",
        sizes: "48x48",
        type: "image/png",
      },
      {
        href: "/images/icons/favicons/favicon-32x32.png",
        rel: "icon",
        sizes: "32x32",
        type: "image/png",
      },
      {
        href: "/images/icons/favicons/favicon-16x16.png",
        rel: "icon",
        sizes: "16x16",
        type: "image/png",
      },
      {
        href: "/images/icons/favicons/favicon-svg.svg",
        rel: "icon",
        sizes: "any",
        type: "image/svg+xml",
      },
      {
        crossOrigin: "use-credentials",
        href: "/site.webmanifest",
        rel: "manifest",
      },
      {
        color: "#19172e",
        href: "/images/icons/safari-pinned-tab.svg",
        rel: "mask-icon",
      },
      { href: "https://fonts.googleapis.com", rel: "preconnect" },
      {
        crossOrigin: "",
        href: "https://fonts.gstatic.com",
        rel: "preconnect",
      },
      {
        href: "https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;0,700;0,900;1,400&display=swap",
        rel: "stylesheet",
      },
    ],
    meta: [
      { charSet: "utf-8" },
      { content: "width=device-width, initial-scale=1", name: "viewport" },
      { content: "#19172e", name: "msapplication-TileColor" },
      { content: "#19172e", name: "theme-color" },
    ],
  }),
});

function RootComponent() {
  const posthogScriptId = useId();
  const toastRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const historyManager = useHistoryManager();

  useServiceWorker();

  // NProgress route transition indicator
  useEffect(() => {
    const unsub1 = router.subscribe("onBeforeNavigate", () => {
      NProgress.start();
    });
    const unsub2 = router.subscribe("onResolved", () => {
      NProgress.done();
    });
    return () => {
      unsub1();
      unsub2();
    };
  }, [router]);

  // Branch SDK lazy init
  useEffect(() => {
    if (!process.env.BRANCH_KEY) {
      return;
    }

    (async () => {
      const branch = (await import("branch-sdk")).default;
      branch.init(process.env.BRANCH_KEY || "");
    })();
  }, []);

  const client = useMemo(() => apollo(), []);
  const splitConfig = useMemo(
    () => ({
      core: {
        authorizationKey: process.env.SPLIT_API_KEY || "",
        key: "key",
      },
    }),
    []
  );

  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <HistoryManagerProvider manager={historyManager}>
          <AnalyticsProvider>
            <ApolloProvider client={client}>
              <Auth0Provider
                authorizationParams={{
                  audience: process.env.AUTH0_AUDIENCE,
                  redirect_uri: `${process.env.APP_URL}/`,
                  scope: process.env.AUTH0_SCOPE,
                }}
                cacheLocation="localstorage"
                clientId={process.env.AUTH0_CLIENT_ID || ""}
                domain={process.env.AUTH0_DOMAIN || ""}
                useRefreshTokens
                useRefreshTokensFallback
              >
                <ErrorBoundary>
                  <SplitFactoryProvider config={splitConfig}>
                    <LocationProvider>
                      <TokenProvider>
                        <meta
                          content={`app-id=1496566338, app-argument=${process.env.APP_URL}${pathname}`}
                          name="apple-itunes-app"
                        />

                        <Outlet />

                        {!pathname.startsWith("/policies/") && <Footer />}

                        {!!process.env.POSTHOG_KEY && (
                          <script
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: posthog said so
                            dangerouslySetInnerHTML={{
                              __html: `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('${process.env.POSTHOG_KEY}',{api_host:'https://app.posthog.com'})`,
                            }}
                            id={posthogScriptId}
                          />
                        )}

                        <AuthModal />
                        <Intercom />
                        <ExploreCategoryModal />
                        <OfflineBanner />
                        <Pageview />
                        <AbandonCart />
                        <Toaster position="top-center" />
                        <div ref={toastRef} />
                      </TokenProvider>
                    </LocationProvider>
                  </SplitFactoryProvider>
                </ErrorBoundary>
              </Auth0Provider>
            </ApolloProvider>
          </AnalyticsProvider>
        </HistoryManagerProvider>
        <Scripts />
      </body>
    </html>
  );
}
